<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import GroupSelect, { type GroupItem } from '~/ui-kit/journal/Performance/GroupSelect.vue'
import type { IncomingStudent, JournalGroup } from '../journal/types'
import { apiPost } from '~/lib/http'

const props = defineProps<{ groups: JournalGroup[] }>()

const isOpen = ref(false)
const groupId = ref<number | undefined>(undefined)
const students = ref<IncomingStudent[]>([])
const fetchingStudentList = ref(true)
const studentFetchError = ref<string | null>(null)

const group = computed(() => props.groups.find(e => e.id === groupId.value))
const groupItems = computed(() =>
  props.groups.map<GroupItem>(g => ({
    value: g.id!,
    color: g.color,
    text: g.name
  }))
)

watch(groupId, val => {
  if (val) fetchStudents()
})

async function fetchStudents() {
  fetchingStudentList.value = true

  try {
    const { data, failed, message } = await apiPost<IncomingStudent[]>('/journal/request_get_jstudents', {
      jgroup_id: groupId.value
    })

    if (failed) throw message

    students.value = data
  } catch (err) {
    let title = 'Произошла ошибка при получении списка учеников. Повторите попытку позже.'
    if (typeof err === 'string') title = err

    studentFetchError.value = title
  }

  fetchingStudentList.value = false
}

export interface CabinetSelectModalExpose {
  $open(groupId?: number): void
}

defineExpose<CabinetSelectModalExpose>({
  $open(group?: number) {
    isOpen.value = true
    if (group) groupId.value = group
  }
})
</script>

<template>
  <v-rich-modal v-model="isOpen" title="Открыть кабинет ученика">
    <div class="dashboard-student-cabinet-modal">
      <div class="dashboard-student-cabinet-modal__notice">
        <v-icon class="blue-text">mdi-information-outline</v-icon>
        Просмотр кабинета ученика доступен для учеников с купленным Умным Учебником соответствующего курса.
      </div>

      <GroupSelect class="mt-3 full-width" :items="groupItems" v-model="groupId" />

      <table class="student-table mt-3" v-if="group">
        <thead>
          <tr>
            <th>Фамилия Имя</th>
            <th>Каб. ученика</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="jstudent in students"
            :key="jstudent.id"
            :class="{ 'student-table__table-row_disabled': !jstudent.smartbook_tariff }"
          >
            <td>
              {{ jstudent.last_name }}
              {{ jstudent.first_name }}
              <span
                v-if="jstudent.smartbook_tariff"
                class="student-table__smartbook-mode-badge ml-half"
                :class="
                  jstudent.smartbook_free_mode
                    ? 'student-table__smartbook-mode-badge_red'
                    : 'student-table__smartbook-mode-badge_green'
                "
              >
                {{ jstudent.smartbook_free_mode ? 'Free' : 'Auto' }}
              </span>
              <span v-if="jstudent.smartbook_tariff" class="ml-half">
                <v-icon v-if="jstudent.smartbook_tariff === 'b'" key="b" class="d-inline-flex"
                  >smartbook-basic-icon</v-icon
                >
                <v-icon v-if="jstudent.smartbook_tariff === 'e'" key="e" class="d-inline-flex">smartbook-icon</v-icon>
              </span>
            </td>
            <td>
              <v-link
                v-if="jstudent.smartbook_tariff"
                mode="primary"
                class="text-body-bold justify-center full-width"
                :href="`/student/impersonate/${jstudent.student_id}`"
                target="_blank"
              >
                Открыть
              </v-link>

              <p v-else class="full-width text-center">Нет УУ</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-rich-modal>
</template>

<style lang="scss" scoped>
.dashboard-student-cabinet-modal {
  max-width: 650px;

  &__notice {
    display: flex;
    gap: 8px;
    border-radius: 8px;
    padding: 12px 16px;

    background: $lightenBlue;
  }
}

.student-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 8px;
    border-bottom: 1px solid $lightGray;

    font-weight: normal;
    background: rgba($lightenGray, 50%);

    white-space: nowrap;

    &:nth-child(2) {
      border-left: 1px solid $lightGray;
    }
  }

  td {
    vertical-align: middle;
    padding: 8px;
    border-bottom: 1px solid $lightGray;

    &:nth-child(1) {
      width: 100%;
    }

    &:nth-child(2) {
      border-left: 1px solid $lightGray;
    }
  }

  td,
  th {
    &:not(:first-child) {
      border-right: 1px solid $lightGray;
    }
  }

  &__table-row_disabled {
    background-color: $lightenGray;
  }

  &__smartbook-mode-badge {
    padding: 2px 4px;
    border-radius: 4px;
    border: 1px solid;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    @include text-caption;

    &_green {
      background: $lightenGreen;
      border-color: $lightGreen;
    }

    &_red {
      background: $lightenRed;
      border-color: $lightRed;
    }
  }
}
</style>
